import ServicePageLayout from './ServicePageLayout';
import { IMG } from '@/content/site';

export default function ShippingCosts() {
  return (
    <ServicePageLayout
      seo={{
        title: 'Shipping Cost Optimization & Invoice Audit | KTK Advisors',
        description:
          'KTK Advisors analyzes carrier contracts, audits parcel invoices, pursues eligible refunds, and brings ongoing visibility to your shipping spend.',
        path: '/solutions/shipping-costs',
      }}
      group="Shipping Cost Optimization"
      headline="Find the opportunities hiding in your shipping spend."
      lede="Parcel agreements are complex and carrier invoices are more error-prone than most businesses realize. KTK Advisors reviews contracts, audits invoices, and pursues eligible refunds—so the spend you already have works harder."
      audienceIntro="For businesses that ship in meaningful volume."
      audience={[
        'Distributors',
        'Manufacturers',
        'Ecommerce businesses',
        'Any business with meaningful parcel-shipping activity',
      ]}
      image={IMG.shipping}
      imageAlt="Warehouse aisle filled with organized shipping inventory"
      problems={[
        {
          t: 'Contracts that outlived their terms',
          d: 'Carrier agreements negotiated years ago rarely match today\'s shipping profile. Without analysis, you will never know what the market would bear.',
        },
        {
          t: 'Invoice errors nobody catches',
          d: 'Parcel invoices are long, complex, and generated at scale. Small billing errors survive quietly, shipment after shipment.',
        },
        {
          t: 'Refunds left unclaimed',
          d: 'Carriers publish service commitments—and eligible refund opportunities attached to them. Pursuing them requires disciplined auditing most teams cannot staff.',
        },
        {
          t: 'No visibility into true cost',
          d: 'When shipping cost is scattered across invoices, surcharges, and accessorial fees, per-order economics are hard to see and harder to manage.',
        },
      ]}
      capabilities={[
        'Carrier contract analysis',
        'Parcel invoice auditing',
        'Pursuit of eligible refund opportunities',
        'Shipping analytics',
        'Ongoing cost visibility and reporting',
        'Coordination of suitable specialized support',
      ]}
      diagram={{
        title: 'Contract to invoice, watched continuously.',
        steps: [
          { n: '01', label: 'Carrier contract' },
          { n: '02', label: 'Invoice audit' },
          { n: '03', label: 'Refund recovery' },
          { n: '04', label: 'Analytics & visibility' },
        ],
      }}
      diagramCaption="The audit runs against the terms in your contract—not generic assumptions. What is recoverable depends on your agreement and carrier programs, and the review explains exactly that."
      helpsIntro="KTK Advisors is your single point of contact—coordinating the analysis, the audit, and the ongoing visibility."
      helps={[
        {
          t: 'Start with your agreements',
          d: 'We review the carrier contracts you operate under and what they actually entitle you to—before talking about changes.',
        },
        {
          t: 'Audit what you already pay',
          d: 'Invoices are checked against the terms and service commitments that apply. Eligible refund opportunities are pursued on your behalf.',
        },
        {
          t: 'Keep the visibility',
          d: 'Analytics turn the spend into something you can manage—by lane, service level, and trend—so decisions improve after the audit too.',
        },
      ]}
      faqs={[
        {
          q: 'What shipping volume qualifies for a review?',
          a: 'It depends on your shipping profile and the programs involved. A brief conversation about your volume and carriers will tell us whether a review makes sense.',
        },
        {
          q: 'Do we have to change carriers?',
          a: 'No. A large part of the value—contract analysis, invoice auditing, refund recovery—applies to the carrier relationships you already have.',
        },
        {
          q: 'How do refunds work?',
          a: 'Major carriers publish service commitments with eligible refund opportunities attached. Pursuing them requires auditing invoices against your agreement. What is recoverable depends on your specific terms, which the review explains.',
        },
        {
          q: 'How is this compensated?',
          a: 'For qualifying engagements, compensation can be tied to the savings achieved. The structure that applies to you is explained clearly before the engagement begins.',
        },
        {
          q: 'What information do you need to start?',
          a: 'A conversation about your shipping activity to start. Please do not include financial account details or other sensitive records in website inquiries.',
        },
      ]}
      cta={{
        title: 'Request a shipping cost review.',
        copy: 'Tell us about your shipping profile—volume, carriers, and what concerns you most. We\'ll tell you whether a review is worth your time.',
        button: 'Request a Shipping Cost Review',
      }}
      interestParam="shipping-costs"
    />
  );
}
